
import { XCircleIcon, PlusIcon, LoaderIcon } from 'vue-feather-icons'
import { getFileName, getProperName } from '~/fc/Helpers/FileHelpers';
import { validateUrlProtocol } from '~/fc/Helpers/StringHelpers';
import { getUrlExtension } from '~/fc/Helpers/FileHelpers';
import { getExt } from "~/fc/Helpers/FileHelpers";
import FileValidationHandler from "~/fc/File/FileValidationHandler";
import { preProcessUrlUploadedFile } from '~/fc/Helpers/ConversionHelper'
import {DMCA_COPYRIGHTED_SOURCES} from '~/fc/constants'
import { uuidv4 } from '~/fc/Helpers/FileHelpers';

export default {
    components: {
        PlusIcon,
        XCircleIcon,
        LoaderIcon,
        // eslint-disable-next-line vue/no-reserved-component-names
        Dialog: () => import(/* webpackChunkName: "Dialog" */ '../../ui/Dialog')
    },
    props: ['single'],
    data: () => ({
        show: false,
        errors: [],
        items: [{ url: '' }],
        submitting: false,
        urlsRenderKey: 0
    }),
    computed: {
        info() { return this.$store.state.info },
        acceptHtml() { return this.info.acceptHtml },
        allowedListFormatted() { return this.allowedList ? this.allowedList.split(',').join(', ') : '' },
        fileValidationHandler() { return new FileValidationHandler(this, this.allowedList) },
        preImportFiles() { return this.info.customVariables?.thumbView },
        allowedList() {
            const onlyUnique = (value, index, self) => {
                return self.indexOf(value) === index;
            };
            if (this.info.customVariables && this.info.customVariables.allowedExts ) {
                return this.info.customVariables.allowedExts;
            } else if (this.info.limitAcceptedFiles && this.info.allowedArr) {
                return this.info.allowedArr.filter(onlyUnique).join(",");
            }
            return '';
        }
    },
    methods: {
        addMore() {
            this.items.push({ url: '' })
        },
        updateItem(i, props) {
            this.items[i] = {...this.items[0], ...props}
            this.urlsRenderKey++
        },
        getNameFromUrl(url) {
            const domain = new URL(url).hostname.replace("www.", "");
            const urlName = url.length > 50 ? `${domain}/....${url.slice(-10)}` : url;
            return this.acceptHtml ? urlName : getProperName(getFileName(url)) || url;
        },
        getExtFromUrl(url) {
            return this.acceptHtml && this.isWebpageExt(getUrlExtension(url))
                ? "webpage"
                : getUrlExtension(url) || "webpage";
        },
        isWebpageExt(ext) {
            return ext ? ["", "htm", "html", "webpage"].includes(ext) : true
        },
        validateCopyright (url) {
            return !DMCA_COPYRIGHTED_SOURCES.some(blockedDomain => url.includes(blockedDomain));
        },
        /**
         * Here we are validating and also populating the File object
         * that would hold the URL. We're serving both purposes
         * together because of the fact that some required information
         * is fetched via the same API we are using to validate the URL.
         * @param {*} item | the URL object
         * @param {*} i 
         */
        async processUrl(url, i) {
            const item = structuredClone(url)
            if (item.url === '') {
                this.errors[i] = this.$t('url_not_empty'); 
                return false;                
            }
            if (!validateUrlProtocol(item.url)) {
                this.errors[i] = this.$t('url_not_start_with'); 
                return false;
            }
            if (!this.validateCopyright(item.url)) {
                this.errors[i] = this.$t('dmca_url_error'); 
                return false;
            }
            item['name'] = item.name || this.getNameFromUrl(item.url)
            item['ext'] = this.getExtFromUrl(item.url)
            item['baseName'] =  item.name ? item.name.replace("." + item.ext, "") : item.url;
            
            try {
                this.$set(this.items, i, {...item, loading: true})
                const {data, status} = await this.$axios.post( process.env.QUERY_SERVICE + "/url-info", { url: item.url, ext: item.ext });
                this.$set(this.items, i, {...item, loading: false})
                let job = null
                if (this.preImportFiles) {
                    this.updateItem(i, { processing: true })
                    job = await preProcessUrlUploadedFile(item)
                }
                const { size, type } = data
            
                if(status !== 200) {
                    this.$set(this.errors, i, this.$t('invalid_url'))
                    return false;
                }
                
                item['total'] = item['done'] = size || 0
                item['type'] = type || 0
                item['ext'] = item.ext || 'webpage';
                item['a'] = item.url
                item['id'] = uuidv4()
                if(job) {
                    item['import'] = job.id
                    item['importTaskId'] = job.tasks.length ? job.tasks[0].id : null
                }
            } catch(e) {
                this.$set(this.errors, i, this.$t('invalid_url'))
                this.$set(this.items, i, {...item, loading: false})
                return false;
            }
                        
            if (!this.fileValidationHandler.validateExt(item.ext, false)) {
                this.$set(this.errors, i, this.$t('url_must_extensions') + this.allowedListFormatted)
                return false;
            }
            
            delete this.errors[i]
            return item
        },
        /**
         * This method processes the URLs and
         * returns true if there are no validation errors.
         */
        async processUrls() {
            const processedUrls = await Promise.all(
                this.items.map((item, i) => this.processUrl(item, i))
            );

            this.items = this.items.map((item, index) => processedUrls[index] || item);

            return this.errors && Object.keys(this.errors).length > 0 ? false : this.items;
        },
        /**
         * We add the processed file to the vuex store in another 
         * component. So we are just passing the processed files 
         * through an event.
         */
        async onSubmit() {
            this.errors = []
            const validItems = await this.processUrls()
            if (!validItems) return            
            this.$emit('submit', validItems)
            this.submitting = true
        },
        open() {
            this.$refs.dialog ? this.$refs.dialog.open() : false
        },
        close() {
            this.submitting = false
            this.$refs.dialog.close()
        },
        clearError(i) {
            this.errors.splice(i, 1)
        },
        removeItem(i) {
            this.items.splice(i, 1)
            this.clearError(i)
        },
        reset() {
            this.items = [{ url: '' }]
            this.errors = []
        }
    }
}
